import { useCallback, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useDrag } from '@use-gesture/react';
import { hapticFeedback } from '@telegram-apps/sdk-react';
import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';
import { Sigil } from '../_nav/Sigil';
import { Wordmark } from '../_nav/Wordmark';

// 04b SCORE PREDICTOR — Wave 3 interaction layer (drag + snap + flip).
// Texts: README.md §5 04b ИГРА (byte-equal). Layout: GAME_DESIGN_DOC_SCORE_PREDICTOR.md §4.
// Visual rules: DESIGN.md §9.5 (match card), §9.6 (wheel picker), §9.7 (pill CTA active),
//   §7.1 (wheel snap 200ms ease-out, flip 700ms spring), §10 (top-left wordmark
//   + flip-back sign-off sigil 24px #5F5F5F per §9.2).
//
// Atmosphere — DESIGN.md §7.8 game variant: reduced gradient, 1 sphere, no shafts.
//
// SCOPE — Wave 3 visual+interaction track: front-face wheels become dragable,
// snap-to-digit with haptic + cyan glow, CTA activates when both wheels touched,
// flip animation reveals back face "Прогноз сохранён" sign-off.
//
// TODO (still deferred — separate backend track, NOT this wave):
//   • D1 persistence — POST predicted score, write users.metadata.predictions JSON.
//   • Worker endpoint /api/match/next — load real match (Zenit/CSKA hardcoded for now).
//   • Cron job — broadcast result via grammY 48h after kickoff. (GDD §8).
//   • Telegram MainButton — none on this screen per DESIGN.md §9.3; in-app pill stays.

export const meta: ModuleMeta = {
  id: '04b-game-predictor',
  title: 'Score Predictor · drag + flip',
  // Igor round-3 2026-06-11: removed from the live showcase ("убираем полностью
  // блок с прогнозом"). Kept as `draft` (not hard-deleted) so the code/glass is
  // recoverable; prediction lives on as the «Лига прогнозистов» card in ИДЕИ.
  status: 'draft',
  viewport: '390x844',
};

interface TeamSlot {
  name: string;
  initial: string;
}

const HOME: TeamSlot = { name: 'ЗЕНИТ', initial: 'З' };
const AWAY: TeamSlot = { name: 'ЦСКА', initial: 'Ц' };

// Wheel-track step in px: §9.6 calls the track 60×120 with three rows, so each
// row (neighbour-up / centre / neighbour-down) is 40px tall. We drag in those
// 40px steps — one row = one digit.
const STEP = 40;

// Defensive Telegram-SDK wrapper. Browser preview falls through silently per
// DESIGN.md §1.4 and the lib/telegram.ts pattern (file untouched).
function trySync<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function TeamChip({ team }: { team: TeamSlot }) {
  return (
    <div className="flex flex-col items-center gap-sp-2">
      <div className="module-04b-team-logo" aria-hidden>
        <span className="font-display text-[1.5rem] leading-none text-text-primary">
          {team.initial}
        </span>
      </div>
      <span className="font-ui text-rubric tracking-[0.2em] text-text-primary">
        {team.name}
      </span>
    </div>
  );
}

interface WheelProps {
  ariaLabel: string;
  value: number;
  onChange: (next: number) => void;
  onTouched: () => void;
  disabled: boolean;
}

/**
 * §9.6 wheel-picker — vertical drag cycles digit 0–9.
 *
 * Render strategy: a transparent 60×120 viewport masks an 11-row stack
 * (digits 0..9 plus a leading 9 wrap-marker isn't needed — we clamp at the
 * ends and let users drag past with a soft rubber-band, but for simplicity
 * v3 clamps strictly).
 *
 * Snap: on `last`, integer round to nearest digit and run a 200ms ease-out
 * transition (DESIGN.md §7.1) on the stack's transform back to the snap
 * position. Snap-glow on the centre slot fires once per landing.
 *
 * Haptic: when the resolved digit crosses an integer boundary during drag
 * (i.e. `nextDigit !== lastEmittedDigit`), fire `selectionChanged()`. Browser
 * preview is silent — the SDK call is wrapped in trySync. (GDD §5.)
 */
function Wheel({ ariaLabel, value, onChange, onTouched, disabled }: WheelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [glow, setGlow] = useState(false);
  const lastDigitRef = useRef<number>(value);
  const reduced = useReducedMotion();

  // Pre-render the digit column once (0..9 from top to bottom). Translate the
  // column so the active digit lands on the centre row of the 3-row window.
  const digits = useMemo(() => Array.from({ length: 10 }, (_, i) => i), []);

  // Centre row sits at y = STEP within the 3×STEP viewport.
  // For a digit `d`, the column needs to translate by (STEP - d*STEP).
  const baseOffset = STEP - value * STEP;
  const liveOffset = baseOffset + dragY;

  const flashSnapGlow = useCallback(() => {
    setGlow(true);
    window.setTimeout(() => setGlow(false), 200);
  }, []);

  const bind = useDrag(
    ({ down, movement: [, my], last, first }) => {
      if (disabled) return;
      if (first) {
        onTouched();
        lastDigitRef.current = value;
      }

      if (down) {
        setIsDragging(true);
        setDragY(my);

        // Resolved "live digit" — what the centre row would read at this
        // instant. We compute it from current value + drag delta.
        const liveDigit = Math.round(value - my / STEP);
        const clamped = Math.max(0, Math.min(9, liveDigit));
        if (clamped !== lastDigitRef.current) {
          lastDigitRef.current = clamped;
          // §9.6 + GDD §5: haptic on every digit crossing during drag.
          // Silent in browser per DESIGN.md §1.4 (trySync no-op).
          trySync(() => hapticFeedback.selectionChanged());
        }
      }

      if (last) {
        setIsDragging(false);
        // Snap to nearest digit (§7.1 wheel snap glow 200ms ease-out).
        const snapped = Math.max(0, Math.min(9, Math.round(value - my / STEP)));
        setDragY(0);
        if (snapped !== value) {
          onChange(snapped);
        }
        flashSnapGlow();
      }
    },
    {
      axis: 'y',
      pointer: { touch: true },
      filterTaps: true,
    },
  );

  // Transition rules: during drag → no transition (raw follow). On release →
  // 200ms ease-out. prefers-reduced-motion collapses to instant.
  const transition = isDragging
    ? 'none'
    : reduced
      ? 'none'
      : 'transform 200ms ease-out';

  return (
    <div
      ref={containerRef}
      className="module-04b-wheel"
      role="spinbutton"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={9}
      aria-valuenow={value}
      aria-disabled={disabled || undefined}
      tabIndex={0}
      {...bind()}
      style={{ touchAction: 'none' }}
    >
      <div
        className="module-04b-wheel-stack"
        style={{
          transform: `translate3d(0, ${liveOffset}px, 0)`,
          transition,
        }}
      >
        {digits.map((d) => (
          <span
            key={d}
            className={
              d === value
                ? 'module-04b-wheel-digit module-04b-wheel-digit--centre'
                : 'module-04b-wheel-digit module-04b-wheel-digit--neighbour'
            }
          >
            {d}
          </span>
        ))}
      </div>

      {/* Snap-glow pulse on landing (§7.1, --shadow-snap-glow, 200ms). */}
      <span
        className={
          glow
            ? 'module-04b-wheel-glow module-04b-wheel-glow--on'
            : 'module-04b-wheel-glow'
        }
        aria-hidden
      />
    </div>
  );
}

export default function PredictorModule() {
  // The flip is CSS-driven (transform + transition) — see .module-04b-flipper in
  // index.css. CSS handles `prefers-reduced-motion` directly via @media query.
  // Wheel's `useReducedMotion` covers the drag-snap transition.
  const [homeDigit, setHomeDigit] = useState(2);
  const [awayDigit, setAwayDigit] = useState(1);
  const [homeTouched, setHomeTouched] = useState(false);
  const [awayTouched, setAwayTouched] = useState(false);
  const [flipped, setFlipped] = useState(false);

  const ctaActive = homeTouched && awayTouched && !flipped;

  const handleSubmit = () => {
    if (!ctaActive) return;
    trySync(() => hapticFeedback.impactOccurred('medium'));
    setFlipped(true);
  };

  return (
    <section
      className="relative min-h-full w-full bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      <AtmosphericLayer spheres={1} shafts={0} intensity="reduced" />

      <Wordmark variant="nav" />

      {/* Igor 2026-06-11 — removed inner `overflow-y-auto` (nested scroll-trap,
          same pattern fixed on 05 МИССИЯ): a module-internal scroll container
          swallows page-level scroll in the seamless flow. Now mirrors 02/05 —
          `min-h-full flex flex-col`, no inner overflow; the outer document owns
          vertical scroll. NB: the wheel keeps `touchAction: 'none'` (vertical
          spin control) below — that is intentional and NOT a scroll-trap. */}
      <div
        className="relative z-10 flex min-h-full flex-col px-sp-6"
        style={{ paddingTop: 'calc(max(var(--safe-top, 0px), 1.25rem) + 56px)' }}
      >
        <p className="font-ui text-rubric uppercase tracking-[0.2em] text-text-muted">
          ИНСТРУМЕНТ ДЛЯ МЕДИА
        </p>

        <h2 className="mt-sp-4 font-display text-display leading-[1.1] tracking-[-0.02em] text-text-primary">
          Прогноз
          <br />
          следующего тура.
        </h2>

        {/* Flip container — 3D perspective wrapper so the card itself can rotateY 180°.
            Mount entry uses `.motion-slide-in` (Wave 3 utility, §3.2). The flipper inside
            owns the actual rotateY transition; CSS keeps the spring on the GPU and
            respects `prefers-reduced-motion` via @media query in index.css. */}
        <div className="module-04b-flip motion-slide-in mt-sp-7">
          <article
            className={
              flipped
                ? 'module-04b-card module-04b-flipper module-04b-flipper--flipped relative rounded-[20px]'
                : 'module-04b-card module-04b-flipper relative rounded-[20px]'
            }
            aria-label="Карточка матча: Зенит против ЦСКА"
          >
            {/* FRONT FACE */}
            <div className="module-04b-face module-04b-face--front p-sp-6">
              <span className="module-04b-rail" aria-hidden />

              <div className="relative z-10 flex items-start justify-between">
                <TeamChip team={HOME} />
                <TeamChip team={AWAY} />
              </div>

              <div className="relative z-10 mt-sp-6 flex items-center justify-center gap-sp-4">
                <Wheel
                  ariaLabel="Счёт Зенита"
                  value={homeDigit}
                  onChange={setHomeDigit}
                  onTouched={() => setHomeTouched(true)}
                  disabled={flipped}
                />
                <span className="font-display text-[2.5rem] leading-none text-text-muted">:</span>
                <Wheel
                  ariaLabel="Счёт ЦСКА"
                  value={awayDigit}
                  onChange={setAwayDigit}
                  onTouched={() => setAwayTouched(true)}
                  disabled={flipped}
                />
              </div>

              {/* CTA pill — disabled until both wheels are dragged at least once,
                  then active per §9.7 (action-primary fill + shadow-cta-active +
                  2.4s scale 1.0 ↔ 1.04 pulse). */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!ctaActive}
                aria-disabled={!ctaActive}
                className={
                  ctaActive
                    ? 'module-04b-cta module-04b-cta--active relative z-10 mt-sp-6 w-full rounded-full px-sp-6 py-sp-3 font-ui text-body'
                    : 'module-04b-cta relative z-10 mt-sp-6 w-full rounded-full px-sp-6 py-sp-3 font-ui text-body'
                }
              >
                зафиксировать прогноз
              </button>
            </div>

            {/* BACK FACE — visible after flip. Text byte-equal to GDD §6 / master
                prompt §5: «Прогноз сохранён» (Lora display-sm) + body (Helvetica
                text-body-sm) + sign-off sigil 24px #5F5F5F (DESIGN.md §9.2). */}
            <div className="module-04b-face module-04b-face--back p-sp-6">
              <span className="module-04b-rail" aria-hidden />
              <div className="flex h-full flex-col items-start justify-center gap-sp-4">
                <h3 className="font-display text-display-sm leading-[1.15] text-text-primary">
                  Прогноз сохранён
                </h3>
                <p className="max-w-[260px] font-ui text-body-sm leading-[1.5] text-text-primary">
                  Через 2 дня сравним с реальным результатом. Бот напишет лично.
                </p>
                <Sigil variant="hud" />
              </div>
            </div>
          </article>
        </div>

        <p className="mt-sp-7 max-w-[320px] font-ui text-body-sm leading-[1.5] text-text-primary">
          Так RAFL/digital собирает прогнозные виджеты для медиа за 4-5 дней. Под РПЛ, КХЛ,
          женский футбол, киберспорт.
        </p>

        <div className="pb-sp-10" />
      </div>

      {/* Universal swipe-up affordance — matches 01-hero / 02-pain pattern. */}
      <div
        className="hint-arrow-pulse pointer-events-none absolute bottom-sp-7 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center gap-sp-3 text-text-muted"
        aria-hidden
      >
        <span className="inline-block h-[14px] w-[14px] rounded-full border border-current" />
        <span className="text-[1.25rem] leading-none">↓</span>
      </div>
    </section>
  );
}
